import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { 
  Users, Calendar, Repeat, Building2, BarChart3, 
  Mail, Phone, MapPin, Clock, CheckCircle, XCircle, 
  RefreshCw, CalendarDays, MessageSquare, X,
  ChevronLeft, ChevronRight, FileText, Upload, Download, Trash2,
  AlertTriangle, TrendingUp, Euro, UserCheck, Bell,
  ArrowLeft, Eye, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ FACTURE UPLOAD MODAL ============
const FactureUploadModal = ({ isOpen, onClose, item, itemType, token, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen || !item) return null;

  const handleUpload = async () => {
    if (!file) { toast.error("Veuillez sélectionner un fichier PDF"); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", item.user_id);
      formData.append("description", description);
      if (itemType === "reservation") formData.append("reservation_id", item.id);
      else if (itemType === "abonnement") formData.append("abonnement_id", item.id);

      const res = await fetch(`${API_URL}/api/admin/facture/upload`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        toast.success("Facture téléversée avec succès");
        setFile(null); setDescription("");
        onSuccess(); onClose();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de l'upload");
      }
    } catch { toast.error("Erreur lors de l'upload"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="p-6 rounded-t-2xl bg-blue-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-full"><FileText className="w-6 h-6 text-blue-600" /></div>
              <div>
                <h3 className="font-bold text-lg text-gray-900">Ajouter une facture</h3>
                <p className="text-sm text-gray-600">{item.user_prenom} {item.user_nom}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fichier PDF *</label>
            <div onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${file ? "border-green-300 bg-green-50" : "border-gray-300 hover:border-blue-300"}`}>
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={(e) => setFile(e.target.files[0])} className="hidden" />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <FileText className="w-5 h-5" /><span className="font-medium">{file.name}</span>
                </div>
              ) : (
                <div className="text-gray-500"><Upload className="w-8 h-8 mx-auto mb-2" /><p className="text-sm">Cliquez pour sélectionner un PDF</p></div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description (optionnel)</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: Facture mars 2026" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button onClick={handleUpload} disabled={loading || !file} className="flex-1 bg-[#003264] hover:bg-[#002244] text-white">
              {loading ? "Envoi..." : "Téléverser"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ DEVIS UPLOAD MODAL ============
const DevisUploadModal = ({ isOpen, onClose, userId, userName, token, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen || !userId) return null;

  const handleUpload = async () => {
    if (!file) { toast.error("Veuillez s\u00e9lectionner un fichier PDF"); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("user_id", userId);
      formData.append("description", description);

      const res = await fetch(`${API_URL}/api/admin/devis/upload`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        toast.success("Devis t\u00e9l\u00e9vers\u00e9 avec succ\u00e8s");
        setFile(null); setDescription("");
        onSuccess(); onClose();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur lors de l'upload");
      }
    } catch { toast.error("Erreur lors de l'upload"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className="p-6 rounded-t-2xl bg-amber-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-full"><FileText className="w-6 h-6 text-amber-600" /></div>
              <div>
                <h3 className="font-bold text-lg text-gray-900">{`D\xe9poser un devis`}</h3>
                <p className="text-sm text-gray-600">{userName}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fichier PDF *</label>
            <div onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${file ? "border-green-300 bg-green-50" : "border-gray-300 hover:border-amber-300"}`}>
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={(e) => setFile(e.target.files[0])} className="hidden" data-testid="devis-file-input" />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <FileText className="w-5 h-5" /><span className="font-medium">{file.name}</span>
                </div>
              ) : (
                <div className="text-gray-500"><Upload className="w-8 h-8 mx-auto mb-2" /><p className="text-sm">{`Cliquez pour s\xe9lectionner un PDF`}</p></div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description (optionnel)</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={`Ex: Devis m\xe9nage avril 2026`} data-testid="devis-description-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button onClick={handleUpload} disabled={loading || !file} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="devis-upload-submit">
              {loading ? "Envoi..." : `T\xe9l\xe9verser`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ ACTION MODAL (CONFIRM/CANCEL) ============
const ActionModal = ({ isOpen, onClose, reservation, actionType, onConfirm }) => {
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setLoading(true);
    await onConfirm(reservation.id, actionType === "confirm" ? "confirme" : "annule", comment);
    setLoading(false); setComment(""); onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
        <div className={`p-6 rounded-t-2xl ${actionType === "confirm" ? "bg-green-50" : "bg-red-50"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {actionType === "confirm" ? (
                <div className="p-3 bg-green-100 rounded-full"><CheckCircle className="w-6 h-6 text-green-600" /></div>
              ) : (
                <div className="p-3 bg-red-100 rounded-full"><XCircle className="w-6 h-6 text-red-600" /></div>
              )}
              <div>
                <h3 className="font-bold text-lg text-gray-900">
                  {actionType === "confirm" ? "Confirmer la réservation" : "Annuler la réservation"}
                </h3>
                <p className="text-sm text-gray-600">{reservation?.user_prenom} {reservation?.user_nom}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="font-medium">{reservation?.date_souhaitee}</span>
              <span className="text-gray-400">à</span>
              <span>{reservation?.heure_souhaitee}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 text-gray-500" /><span>{reservation?.adresse}</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MessageSquare className="w-4 h-4 inline mr-1" />Message pour le client (optionnel)
            </label>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder={actionType === "confirm" ? "Ex: Nous confirmons votre rendez-vous." : "Ex: Nous sommes désolés, nous devons annuler..."}
              className="min-h-[100px] resize-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Retour</Button>
            <Button onClick={handleSubmit} disabled={loading}
              className={`flex-1 ${actionType === "confirm" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"} text-white`}>
              {loading ? "..." : actionType === "confirm" ? "Confirmer" : "Annuler"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ CLIENT DETAIL MODAL ============
const ClientDetailModal = ({ isOpen, onClose, userId, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !userId) return;
    setLoading(true);
    fetch(`${API_URL}/api/admin/client/${userId}`, {
      headers: { "Authorization": `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { toast.error("Erreur chargement fiche client"); setLoading(false); });
  }, [isOpen, userId, token]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="client-detail-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="p-6 bg-[#003264] text-white rounded-t-2xl sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h3 className="font-bold text-lg">Fiche Client</h3>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#003264]"></div>
          </div>
        ) : data ? (
          <div className="p-6 space-y-6">
            {/* Client Info */}
            <div className="flex flex-wrap gap-6 items-start">
              <div className="w-16 h-16 bg-gradient-to-br from-[#003264] to-[#004080] rounded-2xl flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
                {data.user.prenom?.charAt(0)}{data.user.nom?.charAt(0)}
              </div>
              <div className="flex-1 min-w-[200px]">
                <h2 className="text-xl font-bold text-gray-900">{data.user.prenom} {data.user.nom}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge className={data.client_type === "abonnement" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"}>
                    {data.client_type === "abonnement" ? "Abonné" : "Ponctuel"}
                  </Badge>
                  {data.frequence !== "-" && <Badge className="bg-green-100 text-green-800 capitalize">{data.frequence}</Badge>}
                </div>
                <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm text-gray-600">
                  <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{data.user.email}</div>
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{data.user.telephone}</div>
                  <div className="flex items-center gap-2 sm:col-span-2"><MapPin className="w-4 h-4 text-gray-400" />{data.user.adresse}, {data.user.code_postal} {data.user.ville}</div>
                </div>
              </div>
            </div>

            {/* Revenue Summary */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center" data-testid="client-ca-total">
                <p className="text-2xl font-black text-emerald-600">{data.ca_total}€</p>
                <p className="text-xs text-gray-500">CA total réservations</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-black text-purple-600">{data.ca_abonnements_mensuel}€</p>
                <p className="text-xs text-gray-500">CA abonnement/mois</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-2xl font-black text-[#003264]">{data.nb_interventions}</p>
                <p className="text-xs text-gray-500">Interventions</p>
              </div>
            </div>

            {/* Historique des interventions */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#003264]" /> Historique des interventions ({data.reservations.length})
              </h3>
              {data.reservations.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">Aucune intervention</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {data.reservations.map(r => (
                    <div key={r.id} className={`flex items-center justify-between p-3 rounded-xl border text-sm ${
                      r.status === "confirme" ? "bg-green-50 border-green-200" :
                      r.status === "en_attente" ? "bg-amber-50 border-amber-200" :
                      r.status === "annule" ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"
                    }`}>
                      <div>
                        <span className="font-medium">{r.date_souhaitee}</span>
                        <span className="text-gray-400 mx-1">à</span>
                        <span>{r.heure_souhaitee}</span>
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="capitalize">{r.type_prestation}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.prix_total > 0 && <span className="font-bold text-gray-700">{r.prix_total}€</span>}
                        <Badge className={`text-xs ${
                          r.status === "confirme" ? "bg-green-100 text-green-800" :
                          r.status === "en_attente" ? "bg-amber-100 text-amber-800" :
                          "bg-red-100 text-red-800"
                        }`}>
                          {r.status === "confirme" ? "Confirmé" : r.status === "en_attente" ? "En attente" : "Annulé"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Abonnements */}
            {data.abonnements.length > 0 && (
              <div>
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Repeat className="w-5 h-5 text-purple-600" /> Abonnements ({data.abonnements.length})
                </h3>
                <div className="space-y-2">
                  {data.abonnements.map(a => (
                    <div key={a.id} className="p-3 rounded-xl border bg-purple-50 border-purple-200 text-sm flex items-center justify-between">
                      <div>
                        <span className="font-medium capitalize">{a.type_prestation}</span>
                        <span className="mx-2 text-gray-300">|</span>
                        <span className="capitalize">{a.frequence}</span>
                        <span className="mx-2 text-gray-300">|</span>
                        <span>{a.heure}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold">{a.prix_prestation}€</span>
                        <Badge className={a.statut === "actif" ? "bg-green-100 text-green-800" : "bg-orange-100 text-orange-800"}>
                          {a.statut === "actif" ? "Actif" : "Pause"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Factures */}
            {data.factures.length > 0 && (
              <div>
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" /> Factures ({data.factures.length})
                </h3>
                <div className="space-y-2">
                  {data.factures.map(f => (
                    <div key={f.id} className="p-3 rounded-xl border bg-gray-50 border-gray-200 text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-red-500" />
                        <span>{f.filename}</span>
                        {f.description && <span className="text-gray-400">- {f.description}</span>}
                      </div>
                      <Button size="sm" variant="outline" className="text-blue-600 border-blue-200"
                        onClick={() => window.open(`${API_URL}/api/facture/download/${f.id}`, '_blank')}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Devis */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" /> Devis ({data.devis?.length || 0})
              </h3>
              {data.devis?.length > 0 ? (
                <div className="space-y-2">
                  {data.devis.map(d => (
                    <div key={d.id} className="p-3 rounded-xl border bg-amber-50 border-amber-200 text-sm flex items-center justify-between" data-testid={`devis-card-${d.id}`}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-amber-500" />
                        <span>{d.filename}</span>
                        {d.description && <span className="text-gray-400">- {d.description}</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="text-amber-600 border-amber-200"
                          onClick={() => window.open(`${API_URL}/api/devis/download/${d.id}`, '_blank')}>
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200"
                          onClick={async () => {
                            if (!window.confirm("Supprimer ce devis ?")) return;
                            try {
                              await fetch(`${API_URL}/api/admin/devis/${d.id}`, {
                                method: "DELETE",
                                headers: { "Authorization": `Bearer ${token}` }
                              });
                              toast.success("Devis supprim\u00e9");
                              setLoading(true);
                              fetch(`${API_URL}/api/admin/client/${userId}`, { headers: { "Authorization": `Bearer ${token}` } })
                                .then(r => r.json()).then(d => { setData(d); setLoading(false); });
                            } catch { toast.error("Erreur"); }
                          }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">Aucun devis</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-10">Client introuvable</p>
        )}
      </div>
    </div>
  );
};

// ============ CALENDAR VIEW ============
const CalendarView = ({ reservations, abonnements }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const confirmedReservations = reservations.filter(r => r.status === "confirme" || r.status === "en_attente");
  const activeAbonnements = abonnements.filter(a => a.statut === "actif");

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return { daysInMonth: lastDay.getDate(), startingDay: firstDay.getDay(), month, year };
  };

  const { daysInMonth, startingDay, month, year } = getDaysInMonth(currentDate);

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayRes = confirmedReservations.filter(r => r.date_souhaitee === dateStr);
    const dayAbo = activeAbonnements.filter(a => a.prochaine_intervention === dateStr);
    return [...dayRes.map(r => ({ ...r, type: 'reservation' })), ...dayAbo.map(a => ({ ...a, type: 'abonnement' }))];
  };

  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
  const dayNames = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

  const days = [];
  for (let i = 0; i < startingDay; i++) days.push(<div key={`e-${i}`} className="h-24 bg-gray-50"></div>);
  
  for (let day = 1; day <= daysInMonth; day++) {
    const events = getEventsForDay(day);
    const isToday = new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
    days.push(
      <div key={day} className={`min-h-24 border border-gray-100 p-1 ${isToday ? 'bg-blue-50 ring-2 ring-blue-400' : 'bg-white'}`}>
        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>{day}</div>
        <div className="space-y-1">
          {events.slice(0, 3).map((event, idx) => (
            <div key={idx}
              className={`text-xs p-1 rounded truncate ${
                event.status === 'en_attente' ? 'bg-amber-100 text-amber-800' :
                event.type === 'reservation' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
              }`}
              title={`${event.user_prenom} ${event.user_nom} - ${event.type_prestation} - ${event.adresse || ''}`}>
              <span className="font-medium">{event.heure || event.heure_souhaitee}</span> {event.user_prenom}
              {event.adresse && <span className="text-[10px] block opacity-70 truncate">{event.adresse}</span>}
            </div>
          ))}
          {events.length > 3 && <div className="text-xs text-gray-500 pl-1">+{events.length - 3}</div>}
        </div>
      </div>
    );
  }

  const upcomingEvents = [...confirmedReservations, ...activeAbonnements]
    .map(e => ({ ...e, date: e.date_souhaitee || e.prochaine_intervention, time: e.heure_souhaitee || e.heure, type: e.date_souhaitee ? 'reservation' : 'abonnement' }))
    .filter(e => new Date(e.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 10);

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#003264]" />Calendrier des prestations</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="font-semibold text-lg min-w-[160px] text-center">{monthNames[month]} {year}</span>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(year, month + 1, 1))}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-0 border border-gray-200 rounded-lg overflow-hidden">
              {dayNames.map(d => <div key={d} className="bg-[#003264] text-white text-center py-2 text-sm font-medium">{d}</div>)}
              {days}
            </div>
            <div className="flex gap-4 mt-4 text-sm">
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-green-100 rounded"></div><span className="text-gray-600">Confirmées</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-amber-100 rounded"></div><span className="text-gray-600">En attente</span></div>
              <div className="flex items-center gap-2"><div className="w-3 h-3 bg-purple-100 rounded"></div><span className="text-gray-600">Abonnements</span></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div>
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2"><Clock className="w-5 h-5 text-[#003264]" />Prochaines interventions</CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingEvents.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">Aucune intervention à venir</p>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event, idx) => (
                  <div key={idx} className={`p-3 rounded-xl border-l-4 ${
                    event.status === 'en_attente' ? 'border-l-amber-500 bg-amber-50' :
                    event.type === 'reservation' ? 'border-l-green-500 bg-green-50' : 'border-l-purple-500 bg-purple-50'
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900 text-sm">{event.user_prenom} {event.user_nom}</span>
                      <Badge className={`text-xs ${
                        event.status === 'en_attente' ? 'bg-amber-100 text-amber-800' :
                        event.type === 'reservation' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                      }`}>{event.status === 'en_attente' ? 'Att.' : event.type === 'reservation' ? 'Résa' : 'Abo'}</Badge>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />{event.date} à <span className="font-medium">{event.time}</span></div>
                      <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /><span className="truncate">{event.adresse}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============ MAIN ADMIN COMPONENT ============
export const AdminPage = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("dashboard");
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [abonnements, setAbonnements] = useState([]);
  const [demandesPro, setDemandesPro] = useState([]);
  const [factures, setFactures] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [actionType, setActionType] = useState(null);

  const [factureModalOpen, setFactureModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);

  // Client detail
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Devis modal
  const [devisModalOpen, setDevisModalOpen] = useState(false);
  const [devisUserId, setDevisUserId] = useState(null);
  const [devisUserName, setDevisUserName] = useState("");

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) {
      navigate("/connexion", { state: { returnTo: "/admin", requireAdmin: true } });
    }
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" };
      const [statsRes, usersRes, reservationsRes, abonnementsRes, demandesRes, facturesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/users`, { headers }),
        fetch(`${API_URL}/api/admin/reservations`, { headers }),
        fetch(`${API_URL}/api/admin/abonnements`, { headers }),
        fetch(`${API_URL}/api/admin/demandes-pro`, { headers }),
        fetch(`${API_URL}/api/admin/factures`, { headers })
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (reservationsRes.ok) setReservations(await reservationsRes.json());
      if (abonnementsRes.ok) setAbonnements(await abonnementsRes.json());
      if (demandesRes.ok) setDemandesPro(await demandesRes.json());
      if (facturesRes.ok) setFactures(await facturesRes.json());
    } catch { toast.error("Erreur lors du chargement des données"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!authLoading && user?.role === "admin" && token) fetchData();
  }, [user, token, authLoading]);

  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "-";

  const getStatusBadge = (status) => {
    const c = {
      en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
      confirme: { label: "Confirmé", cls: "bg-green-100 text-green-800 border border-green-200" },
      termine: { label: "Terminé", cls: "bg-blue-100 text-blue-800 border border-blue-200" },
      annule: { label: "Annulé", cls: "bg-red-100 text-red-800 border border-red-200" },
      actif: { label: "Actif", cls: "bg-green-100 text-green-800 border border-green-200" },
      pause: { label: "En pause", cls: "bg-orange-100 text-orange-800 border border-orange-200" },
    };
    const cfg = c[status] || { label: status, cls: "bg-gray-100 text-gray-800" };
    return <Badge className={`${cfg.cls} font-medium`}>{cfg.label}</Badge>;
  };

  const openActionModal = (res, type) => { setSelectedReservation(res); setActionType(type); setModalOpen(true); };
  const openFactureModal = (item, type) => { setSelectedItem(item); setSelectedItemType(type); setFactureModalOpen(true); };
  const openClientDetail = (userId) => { setSelectedUserId(userId); setClientModalOpen(true); };
  const openDevisModal = (userId, userName) => { setDevisUserId(userId); setDevisUserName(userName); setDevisModalOpen(true); };

  const updateReservationStatus = async (id, newStatus, comment) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reservation/${id}/status`, {
        method: "PATCH",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus, comment_admin: comment })
      });
      if (res.ok) {
        toast.success(newStatus === "confirme" ? "Réservation confirmée" : "Réservation annulée");
        fetchData();
      }
    } catch { toast.error("Erreur lors de la mise à jour"); }
  };

  const deleteUser = async (userId) => {
    if (!confirm("Supprimer ce client et toutes ses données ?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/user/${userId}`, { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } });
      if (res.ok) { toast.success("Client supprimé"); fetchData(); }
    } catch { toast.error("Erreur"); }
  };

  const deleteReservation = async (id) => {
    if (!confirm("Supprimer cette réservation ?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/reservation/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } });
      if (res.ok) { toast.success("Réservation supprimée"); fetchData(); }
    } catch { toast.error("Erreur"); }
  };

  const deleteAbonnement = async (id) => {
    if (!confirm("Supprimer cet abonnement ?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/abonnement/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } });
      if (res.ok) { toast.success("Abonnement supprimé"); fetchData(); }
    } catch { toast.error("Erreur"); }
  };

  const deleteDemandePro = async (id) => {
    if (!confirm("Supprimer cette demande ?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/demande-pro/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } });
      if (res.ok) { toast.success("Demande supprimée"); fetchData(); }
    } catch { toast.error("Erreur"); }
  };

  const deleteFacture = async (id) => {
    if (!confirm("Supprimer cette facture ?")) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/facture/${id}`, { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } });
      if (res.ok) { toast.success("Facture supprimée"); fetchData(); }
    } catch { toast.error("Erreur"); }
  };

  const clearAll = async (collection) => {
    const labels = { users: "tous les clients", reservations: "toutes les réservations", abonnements: "tous les abonnements", demandes_pro: "toutes les demandes pro", factures: "toutes les factures" };
    if (!confirm(`Supprimer ${labels[collection]} ?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/clear-all/${collection}`, { method: "DELETE", headers: { "Authorization": `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); toast.success(`${d.deleted} supprimé(s)`); fetchData(); }
    } catch { toast.error("Erreur"); }
  };

  if (authLoading) return (
    <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003264]"></div>
    </div>
  );

  if (!user || user.role !== "admin") return null;

  const pendingReservations = reservations.filter(r => r.status === "en_attente");
  const confirmedReservations = reservations.filter(r => r.status === "confirme");

  // Compute per-client revenue for clients tab
  const getClientRevenue = (userId) => stats?.revenue_per_client?.[userId] || 0;
  const getClientType = (userId) => {
    const hasAbo = abonnements.some(a => a.user_id === userId && a.statut === "actif");
    return hasAbo ? "abonnement" : "ponctuel";
  };
  const getClientFrequence = (userId) => {
    const abo = abonnements.find(a => a.user_id === userId && a.statut === "actif");
    return abo?.frequence || "-";
  };

  return (
    <div className="min-h-screen bg-gray-100 pt-20" data-testid="admin-page">
      <div className="bg-gradient-to-r from-[#003264] to-[#004080] text-white py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Espace Administration</h1>
              <p className="text-white/70 mt-1">Gérez vos clients, réservations et chiffre d'affaires</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="refresh-data-btn">
              <RefreshCw className="w-4 h-4 mr-2" />Actualiser
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003264]"></div>
          </div>
        ) : (
          <>
            {/* ===== REVENUE + KEY STATS ===== */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6" data-testid="admin-stats">
                <Card className="bg-white border-0 shadow-sm col-span-2 md:col-span-2">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-3 bg-emerald-50 rounded-xl"><Euro className="w-6 h-6 text-emerald-600" /></div>
                      <p className="text-sm font-medium text-gray-500">Chiffre d'affaires</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div data-testid="ca-jour">
                        <p className="text-2xl font-bold text-emerald-600">{stats.ca_jour}€</p>
                        <p className="text-xs text-gray-400">Aujourd'hui</p>
                      </div>
                      <div data-testid="ca-semaine">
                        <p className="text-2xl font-bold text-emerald-600">{stats.ca_semaine}€</p>
                        <p className="text-xs text-gray-400">Semaine</p>
                      </div>
                      <div data-testid="ca-mois">
                        <p className="text-2xl font-bold text-emerald-600">{stats.ca_mois}€</p>
                        <p className="text-xs text-gray-400">Mois</p>
                      </div>
                    </div>
                    {stats.ca_abonnements_mois > 0 && (
                      <p className="text-xs text-gray-400 mt-2 pt-2 border-t">+ {stats.ca_abonnements_mois}€ récurrent (abonnements)</p>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold text-[#003264]">{stats.clients_actifs}</p>
                        <p className="text-sm text-gray-500 mt-1">Clients actifs</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-xl"><UserCheck className="w-6 h-6 text-[#003264]" /></div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold text-amber-600">{pendingReservations.length}</p>
                        <p className="text-sm text-gray-500 mt-1">En attente</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-xl"><Clock className="w-6 h-6 text-amber-600" /></div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold text-green-600">{confirmedReservations.length}</p>
                        <p className="text-sm text-gray-500 mt-1">Confirmées</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-xl"><CheckCircle className="w-6 h-6 text-green-600" /></div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-3xl font-bold text-purple-600">{stats.abonnements_actifs}</p>
                        <p className="text-sm text-gray-500 mt-1">Abonnements</p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-xl"><Repeat className="w-6 h-6 text-purple-600" /></div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* ===== ALERTS ===== */}
            {stats?.alerts?.length > 0 && (
              <div className="mb-6 space-y-2" data-testid="admin-alerts">
                {stats.alerts.map((alert, i) => (
                  <div key={i} className={`flex items-center gap-3 p-4 rounded-xl border ${
                    alert.type === "danger" ? "bg-red-50 border-red-200 text-red-800" :
                    alert.type === "warning" ? "bg-amber-50 border-amber-200 text-amber-800" :
                    "bg-blue-50 border-blue-200 text-blue-800"
                  }`}>
                    <Bell className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium text-sm">{alert.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* ===== TABS ===== */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="bg-white p-1.5 rounded-xl shadow-sm border inline-flex flex-wrap gap-1">
                <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#003264] data-[state=active]:text-white rounded-lg px-4">
                  <BarChart3 className="w-4 h-4 mr-2" />Aperçu
                </TabsTrigger>
                <TabsTrigger value="calendar" className="data-[state=active]:bg-[#003264] data-[state=active]:text-white rounded-lg px-4">
                  <CalendarDays className="w-4 h-4 mr-2" />Planning
                </TabsTrigger>
                <TabsTrigger value="reservations" className="data-[state=active]:bg-[#003264] data-[state=active]:text-white rounded-lg px-4">
                  <Calendar className="w-4 h-4 mr-2" />Réservations
                </TabsTrigger>
                <TabsTrigger value="abonnements" className="data-[state=active]:bg-[#003264] data-[state=active]:text-white rounded-lg px-4">
                  <Repeat className="w-4 h-4 mr-2" />Abonnements
                </TabsTrigger>
                <TabsTrigger value="clients" className="data-[state=active]:bg-[#003264] data-[state=active]:text-white rounded-lg px-4">
                  <Users className="w-4 h-4 mr-2" />Clients
                </TabsTrigger>
                <TabsTrigger value="factures" className="data-[state=active]:bg-[#003264] data-[state=active]:text-white rounded-lg px-4">
                  <FileText className="w-4 h-4 mr-2" />Factures
                </TabsTrigger>
                <TabsTrigger value="pro" className="data-[state=active]:bg-[#003264] data-[state=active]:text-white rounded-lg px-4">
                  <Building2 className="w-4 h-4 mr-2" />Pro
                </TabsTrigger>
              </TabsList>

              {/* ===== DASHBOARD TAB ===== */}
              <TabsContent value="dashboard" className="space-y-6">
                {pendingReservations.length > 0 && (
                  <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
                    <CardHeader className="pb-3 bg-amber-50/50">
                      <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                        <Clock className="w-5 h-5" />Réservations en attente ({pendingReservations.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {pendingReservations.map(res => (
                          <div key={res.id} className="bg-white border-2 border-amber-100 rounded-xl p-4 hover:shadow-md transition">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="font-bold text-gray-900">{res.user_prenom} {res.user_nom}</h4>
                                <p className="text-sm text-gray-500">{res.user_telephone}</p>
                              </div>
                              {getStatusBadge(res.status)}
                            </div>
                            <div className="space-y-2 mb-4">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="w-4 h-4 text-[#003264]" />
                                <span className="font-semibold">{res.date_souhaitee} à {res.heure_souhaitee}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <MapPin className="w-4 h-4 text-gray-400" /><span className="truncate">{res.adresse}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700 capitalize">{res.type_prestation}</span>
                                {res.prix_total > 0 && <span className="text-sm font-bold text-[#003264]">{res.prix_total}€</span>}
                                {res.avance_credit_impot && (
                                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">Avance CI</span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => openActionModal(res, "confirm")}>
                                <CheckCircle className="w-4 h-4 mr-1" />Valider
                              </Button>
                              <Button size="sm" variant="outline" className="flex-1 border-red-200 text-red-600 hover:bg-red-50" onClick={() => openActionModal(res, "cancel")}>
                                <XCircle className="w-4 h-4 mr-1" />Refuser
                              </Button>
                            </div>
                            {res.avance_credit_impot && res.credit_impot_data && (
                              <div className="mt-3 pt-3 border-t border-amber-200 bg-emerald-50 rounded-lg p-3">
                                <p className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1"><Euro className="w-3 h-3" /> Avance immédiate crédit d'impôt</p>
                                <div className="grid grid-cols-2 gap-1 text-xs text-gray-700">
                                  <p><strong>Nom :</strong> {res.credit_impot_data.ci_nom} {res.credit_impot_data.ci_prenom}</p>
                                  <p><strong>Naissance :</strong> {res.credit_impot_data.ci_date_naissance}</p>
                                  <p><strong>E-mail :</strong> {res.credit_impot_data.ci_email}</p>
                                  <p><strong>Tél :</strong> {res.credit_impot_data.ci_telephone}</p>
                                  <p className="col-span-2"><strong>N° fiscal :</strong> {res.credit_impot_data.ci_numero_fiscal}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Quick Planning View */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CalendarDays className="w-5 h-5 text-[#003264]" />Planning rapide (7 prochains jours)
                      </CardTitle>
                      <Button variant="outline" size="sm" onClick={() => setActiveTab("calendar")} className="text-[#003264]">
                        Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const next7Days = [];
                      const today = new Date();
                      for (let i = 0; i < 7; i++) {
                        const d = new Date(today);
                        d.setDate(today.getDate() + i);
                        const dateStr = d.toISOString().split('T')[0];
                        const dayRes = reservations.filter(r => r.date_souhaitee === dateStr && r.status !== "annule");
                        const dayAbo = abonnements.filter(a => a.prochaine_intervention === dateStr && a.statut === "actif");
                        const events = [...dayRes.map(r => ({ ...r, evtType: 'res' })), ...dayAbo.map(a => ({ ...a, evtType: 'abo' }))];
                        next7Days.push({ date: dateStr, day: d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }), events, isToday: i === 0 });
                      }
                      return (
                        <div className="grid grid-cols-7 gap-2" data-testid="quick-planning">
                          {next7Days.map(day => (
                            <div key={day.date} className={`rounded-xl p-3 min-h-[120px] ${day.isToday ? 'bg-blue-50 ring-2 ring-blue-400' : 'bg-gray-50'}`}>
                              <p className={`text-xs font-semibold mb-2 text-center ${day.isToday ? 'text-blue-600' : 'text-gray-500'}`}>{day.day}</p>
                              {day.events.length === 0 ? (
                                <p className="text-xs text-gray-300 text-center">-</p>
                              ) : (
                                <div className="space-y-1">
                                  {day.events.slice(0, 3).map((e, i) => (
                                    <div key={i} className={`text-[10px] p-1 rounded truncate ${
                                      e.status === 'en_attente' ? 'bg-amber-100 text-amber-800' :
                                      e.evtType === 'res' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                                    }`}>
                                      <span className="font-medium">{e.heure_souhaitee || e.heure}</span> {e.user_prenom}
                                    </div>
                                  ))}
                                  {day.events.length > 3 && <p className="text-[10px] text-gray-400 text-center">+{day.events.length-3}</p>}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2"><CheckCircle className="w-5 h-5 text-green-600" />Dernières confirmations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {confirmedReservations.length === 0 ? (
                        <p className="text-gray-500 text-sm py-4 text-center">Aucune</p>
                      ) : (
                        <div className="space-y-3">
                          {confirmedReservations.slice(0, 5).map(res => (
                            <div key={res.id} className="flex items-center justify-between p-3 bg-green-50 rounded-xl cursor-pointer hover:bg-green-100 transition"
                              onClick={() => openClientDetail(res.user_id)}>
                              <div>
                                <p className="font-medium text-gray-900">{res.user_prenom} {res.user_nom}</p>
                                <p className="text-sm text-gray-500">{res.date_souhaitee} à {res.heure_souhaitee}</p>
                              </div>
                              <div className="text-right">
                                <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full capitalize">{res.type_prestation}</span>
                                {res.prix_total > 0 && <p className="text-sm font-bold text-green-700 mt-1">{res.prix_total}€</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-[#003264]" />Derniers clients inscrits</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {users.length === 0 ? (
                        <p className="text-gray-500 text-sm py-4 text-center">Aucun</p>
                      ) : (
                        <div className="space-y-3">
                          {users.slice(0, 5).map(u => (
                            <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition"
                              onClick={() => openClientDetail(u.id)}>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#003264] rounded-full flex items-center justify-center text-white font-bold text-sm">
                                  {u.prenom?.charAt(0)}{u.nom?.charAt(0)}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{u.prenom} {u.nom}</p>
                                  <p className="text-xs text-gray-500">{u.telephone}</p>
                                </div>
                              </div>
                              <p className="text-xs text-gray-400">{formatDate(u.created_at)}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* ===== CALENDAR TAB ===== */}
              <TabsContent value="calendar">
                <CalendarView reservations={reservations} abonnements={abonnements} />
              </TabsContent>

              {/* ===== RESERVATIONS TAB ===== */}
              <TabsContent value="reservations">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-[#003264]" />Toutes les réservations ({reservations.length})</CardTitle>
                    {reservations.length > 0 && (
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => clearAll("reservations")}>
                        <Trash2 className="w-4 h-4 mr-1" />Tout supprimer
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {reservations.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucune réservation</p>
                    ) : (
                      <div className="space-y-4">
                        {reservations.map(res => (
                          <div key={res.id} className={`p-4 rounded-xl border-2 ${
                            res.status === 'en_attente' ? 'border-amber-200 bg-amber-50/30' :
                            res.status === 'confirme' ? 'border-green-200 bg-green-50/30' :
                            res.status === 'annule' ? 'border-red-200 bg-red-50/30' : 'border-gray-200'
                          }`}>
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <div className="flex-1 min-w-[200px]">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-bold text-lg text-gray-900 cursor-pointer hover:text-[#003264] transition" onClick={() => openClientDetail(res.user_id)}>
                                    {res.user_prenom} {res.user_nom}
                                  </h4>
                                  {getStatusBadge(res.status)}
                                </div>
                                <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
                                  <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{res.user_email}</div>
                                  <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{res.user_telephone}</div>
                                </div>
                              </div>
                              <div className="flex-1 min-w-[200px]">
                                <div className="flex items-center gap-2 mb-2">
                                  <Calendar className="w-4 h-4 text-[#003264]" />
                                  <span className="font-semibold">{res.date_souhaitee} à {res.heure_souhaitee}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                  <MapPin className="w-4 h-4 text-gray-400" />{res.adresse}
                                </div>
                                <div className="mt-2 flex items-center gap-2">
                                  <span className="px-3 py-1 bg-gray-100 rounded-full text-sm capitalize">{res.type_prestation}</span>
                                  {res.prix_total > 0 && <span className="font-bold text-[#003264]">{res.prix_total}€</span>}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {res.status === 'en_attente' && (
                                  <>
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => openActionModal(res, "confirm")}>
                                      <CheckCircle className="w-4 h-4 mr-1" />Valider
                                    </Button>
                                    <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => openActionModal(res, "cancel")}>
                                      <XCircle className="w-4 h-4 mr-1" />Refuser
                                    </Button>
                                  </>
                                )}
                                {res.status === 'confirme' && (
                                  <>
                                    <Button size="sm" variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50" onClick={() => openFactureModal(res, "reservation")}>
                                      <FileText className="w-4 h-4 mr-1" />Facture
                                    </Button>
                                    <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteReservation(res.id)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {res.status === 'annule' && (
                                  <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => deleteReservation(res.id)}>
                                    <Trash2 className="w-4 h-4 mr-1" />Supprimer
                                  </Button>
                                )}
                              </div>
                            </div>
                            {res.comment_admin && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex items-start gap-2 text-sm">
                                  <MessageSquare className="w-4 h-4 text-[#003264] mt-0.5" />
                                  <div><span className="font-medium text-[#003264]">Commentaire :</span><p className="text-gray-600 mt-1">{res.comment_admin}</p></div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ===== CLIENTS TAB (Complete Fiche Client) ===== */}
              <TabsContent value="clients">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-[#003264]" />Fichier Clients ({users.length})</CardTitle>
                    {users.length > 0 && (
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => clearAll("users")}>
                        <Trash2 className="w-4 h-4 mr-1" />Tout supprimer
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {users.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucun client</p>
                    ) : (
                      <div className="space-y-3" data-testid="clients-list">
                        {users.map(u => {
                          const clientType = getClientType(u.id);
                          const freq = getClientFrequence(u.id);
                          const revenue = getClientRevenue(u.id);
                          const nbRes = reservations.filter(r => r.user_id === u.id).length;
                          return (
                            <div key={u.id} className="p-4 bg-gray-50 rounded-xl hover:shadow-md transition group border border-transparent hover:border-gray-200">
                              <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-[250px]">
                                  <div className="w-12 h-12 bg-gradient-to-br from-[#003264] to-[#004080] rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                                    {u.prenom?.charAt(0)}{u.nom?.charAt(0)}
                                  </div>
                                  <div>
                                    <h4 className="font-bold text-gray-900">{u.prenom} {u.nom}</h4>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Badge className={clientType === "abonnement" ? "bg-purple-100 text-purple-800 text-xs" : "bg-blue-100 text-blue-800 text-xs"}>
                                        {clientType === "abonnement" ? "Abonné" : "Ponctuel"}
                                      </Badge>
                                      {freq !== "-" && <Badge className="bg-green-100 text-green-800 text-xs capitalize">{freq}</Badge>}
                                    </div>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 text-sm flex-1 min-w-[300px]">
                                  <div className="flex items-center gap-2 text-gray-600"><Phone className="w-3.5 h-3.5 text-gray-400" />{u.telephone}</div>
                                  <div className="flex items-center gap-2 text-gray-600"><Mail className="w-3.5 h-3.5 text-gray-400" /><span className="truncate">{u.email}</span></div>
                                  <div className="flex items-center gap-2 text-gray-600"><MapPin className="w-3.5 h-3.5 text-gray-400" />{u.code_postal} {u.ville}</div>
                                  <div className="flex items-center gap-2 text-gray-600"><Calendar className="w-3.5 h-3.5 text-gray-400" />{nbRes} intervention{nbRes > 1 ? 's' : ''}</div>
                                </div>

                                <div className="flex items-center gap-4">
                                  <div className="text-right">
                                    <p className="text-lg font-bold text-emerald-600">{revenue}€</p>
                                    <p className="text-xs text-gray-400">CA total</p>
                                  </div>
                                  <Button size="sm" variant="outline" className="border-[#003264] text-[#003264] hover:bg-[#003264] hover:text-white"
                                    onClick={() => openClientDetail(u.id)} data-testid={`client-detail-btn-${u.id}`}>
                                    <Eye className="w-4 h-4 mr-1" />Fiche
                                  </Button>
                                  <Button size="sm" variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white"
                                    onClick={() => openDevisModal(u.id, `${u.prenom} ${u.nom}`)} data-testid={`client-devis-btn-${u.id}`}>
                                    <FileText className="w-4 h-4 mr-1" />Devis
                                  </Button>
                                  <button onClick={() => deleteUser(u.id)}
                                    className="p-1.5 bg-red-100 text-red-600 rounded-lg opacity-0 group-hover:opacity-100 transition hover:bg-red-200" title="Supprimer">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ===== ABONNEMENTS TAB ===== */}
              <TabsContent value="abonnements">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><Repeat className="w-5 h-5 text-[#003264]" />Abonnements ({abonnements.length})</CardTitle>
                    {abonnements.length > 0 && (
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => clearAll("abonnements")}>
                        <Trash2 className="w-4 h-4 mr-1" />Tout supprimer
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {abonnements.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucun abonnement</p>
                    ) : (
                      <div className="space-y-4">
                        {abonnements.map(abo => (
                          <div key={abo.id} className={`p-4 rounded-xl border-2 relative ${
                            abo.statut === 'actif' ? 'border-green-200 bg-green-50/30' :
                            abo.statut === 'pause' ? 'border-orange-200 bg-orange-50/30' : 'border-gray-200'
                          }`}>
                            <button onClick={() => deleteAbonnement(abo.id)} className="absolute top-3 right-3 p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition">
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-bold text-lg text-gray-900 cursor-pointer hover:text-[#003264]" onClick={() => openClientDetail(abo.user_id)}>
                                    {abo.user_prenom} {abo.user_nom}
                                  </h4>
                                  {getStatusBadge(abo.statut)}
                                </div>
                                <p className="text-sm text-gray-500">{abo.user_email} - {abo.user_telephone}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-[#003264]">{abo.prix_prestation}€</p>
                                <p className="text-sm text-green-600">{abo.prix_apres_credit}€ après crédit</p>
                              </div>
                            </div>
                            <div className="grid sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
                              <div>
                                <p className="text-xs text-gray-500 uppercase mb-1">Service</p>
                                <p className="font-medium capitalize">{abo.type_prestation}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase mb-1">Fréquence</p>
                                <p className="font-medium capitalize">{abo.frequence}</p>
                                <p className="text-sm text-gray-500">{abo.jour_semaine && `Le ${abo.jour_semaine}`}{abo.jour_mois && `Le ${abo.jour_mois} du mois`} à {abo.heure}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase mb-1">Prochaine intervention</p>
                                <p className="font-medium">{abo.prochaine_intervention}</p>
                                <p className="text-sm text-gray-500 truncate">{abo.adresse}</p>
                                {abo.statut === 'actif' && (
                                  <Button size="sm" variant="outline" className="mt-2 border-blue-200 text-blue-600 hover:bg-blue-50" onClick={() => openFactureModal(abo, "abonnement")}>
                                    <FileText className="w-4 h-4 mr-1" />Facture
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ===== FACTURES TAB ===== */}
              <TabsContent value="factures">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#003264]" />Factures ({factures.length})</CardTitle>
                    {factures.length > 0 && (
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => clearAll("factures")}>
                        <Trash2 className="w-4 h-4 mr-1" />Tout supprimer
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {factures.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">Aucune facture</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {factures.map(f => (
                          <div key={f.id} className="p-4 rounded-xl border-2 border-gray-200 hover:border-blue-200 transition">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                              <div className="flex items-center gap-4">
                                <div className="p-3 bg-red-50 rounded-xl"><FileText className="w-6 h-6 text-red-600" /></div>
                                <div>
                                  <h4 className="font-bold text-gray-900">{f.filename}</h4>
                                  <p className="text-sm text-gray-500">{f.user_prenom} {f.user_nom} - {f.prestation_type}</p>
                                  {f.description && <p className="text-xs text-gray-400 mt-1">{f.description}</p>}
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <p className="text-xs text-gray-400">{new Date(f.created_at).toLocaleDateString("fr-FR")}</p>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="outline" className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                    onClick={() => window.open(`${API_URL}/api/facture/download/${f.id}`, '_blank')}><Download className="w-4 h-4" /></Button>
                                  <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => deleteFacture(f.id)}><Trash2 className="w-4 h-4" /></Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ===== PRO TAB ===== */}
              <TabsContent value="pro">
                <Card className="border-0 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#003264]" />Demandes pro ({demandesPro.length})</CardTitle>
                    {demandesPro.length > 0 && (
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => clearAll("demandes_pro")}>
                        <Trash2 className="w-4 h-4 mr-1" />Tout supprimer
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    {demandesPro.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Aucune demande</p>
                    ) : (
                      <div className="space-y-4">
                        {demandesPro.map(d => (
                          <div key={d.id} className="p-4 rounded-xl border-2 border-gray-200 hover:shadow-md transition relative">
                            <button onClick={() => deleteDemandePro(d.id)} className="absolute top-3 right-3 p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <div className="flex items-center gap-3 mb-3">
                              <div className="p-2 bg-[#003264]/10 rounded-lg"><Building2 className="w-5 h-5 text-[#003264]" /></div>
                              <div><h4 className="font-bold text-gray-900">{d.prenom} {d.nom}</h4><p className="text-sm text-gray-500">{d.type_activite}</p></div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600">
                              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{d.email}</div>
                              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{d.telephone}</div>
                            </div>
                            {d.message && <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{d.message}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Modals */}
      <ActionModal isOpen={modalOpen} onClose={() => setModalOpen(false)} reservation={selectedReservation} actionType={actionType} onConfirm={updateReservationStatus} />
      <FactureUploadModal isOpen={factureModalOpen} onClose={() => setFactureModalOpen(false)} item={selectedItem} itemType={selectedItemType} token={token} onSuccess={fetchData} />
      <ClientDetailModal isOpen={clientModalOpen} onClose={() => setClientModalOpen(false)} userId={selectedUserId} token={token} />
      <DevisUploadModal isOpen={devisModalOpen} onClose={() => setDevisModalOpen(false)} userId={devisUserId} userName={devisUserName} token={token} onSuccess={fetchData} />
    </div>
  );
};
